import { format, parseISO } from "date-fns"
import { exactCurrencyFormatter } from "../../../../utils"
import { Promotion } from "../types"
import { MarginImpact, format_money, format_pct, unit_discount } from "../deal_math"
import { PROMOTION_CATEGORY_LABELS, promotion_type_layout } from "../promotion_type_layout"

export interface SummaryMissingField {
  /** What is missing, as the rep reads it: "a customer". */
  label: string
  /** DOM id of the field to scroll to. */
  anchor: string
}

interface PromotionSummaryPanelProps {
  promotion: Promotion
  calendar_name?: string
  /** The Expected trade estimate, when the deal can be priced. */
  estimate: number | null
  margin: MarginImpact | null
  owner_name?: string | null
  approver_name?: string | null
  missing: SummaryMissingField[]
  /** Red-highlight the missing fields (after the rep tried to save). */
  attempted: boolean
  file_count: number
  className?: string
}

/** PURE: "Mar 2 – Mar 29, 2026", "from Mar 2, 2026", or null with no dates. */
export function format_date_span(start?: string, end?: string): string | null {
  const start_date = start ? parseISO(start) : null
  const end_date = end ? parseISO(end) : null
  if (start_date && end_date) {
    const same_year = start_date.getFullYear() === end_date.getFullYear()
    return `${format(start_date, same_year ? "MMM d" : "MMM d, yyyy")} – ${format(end_date, "MMM d, yyyy")}`
  }
  if (start_date) return `from ${format(start_date, "MMM d, yyyy")}`
  if (end_date) return `until ${format(end_date, "MMM d, yyyy")}`
  return null
}

/** PURE: the deal in a phrase — "$1.50 off per unit", "15% off", "2 for $6.00", "Buy 1 get 1 free". */
export function describe_deal(promotion: Promotion): string | null {
  const layout = promotion_type_layout(promotion.promotion_type)
  switch (layout.deal_fields) {
    case "discount": {
      const parts: string[] = []
      if (promotion.discount_amount) parts.push(`${format_money(promotion.discount_amount)} off per unit`)
      if (promotion.discount_percentage) parts.push(`${format_pct(promotion.discount_percentage)} off`)
      return parts.join(" · ") || null
    }
    case "multibuy":
      return promotion.multibuy_quantity && promotion.multibuy_price ? `${promotion.multibuy_quantity} for ${format_money(promotion.multibuy_price)}` : null
    case "bogo":
      return promotion.bogo_buy_quantity && promotion.bogo_get_quantity ? `Buy ${promotion.bogo_buy_quantity} get ${promotion.bogo_get_quantity} free` : null
    case "none":
      return null
  }
}

function Row({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <div class="flex items-start justify-between gap-3 text-sm">
      <dt class="shrink-0 text-gray-500">{label}</dt>
      <dd class="min-w-0 text-right text-gray-900">{children}</dd>
    </div>
  )
}

/**
 * The promotion at a glance, kept beside the form: type, account, dates, products, deal, spend,
 * margin, owner and status, plus what is still needed. Bakhrom (1st Phorm) asked for "a short
 * summary bar on the right showing type, dates, total planned spend and the products chosen",
 * because the form's helper text pushed the useful parts far down the page.
 */
export function PromotionSummaryPanel({
  promotion,
  calendar_name,
  estimate,
  margin,
  owner_name,
  approver_name,
  missing,
  attempted,
  file_count,
  className,
}: PromotionSummaryPanelProps) {
  const layout = promotion_type_layout(promotion.promotion_type)
  const deal = describe_deal(promotion)
  const unit = unit_discount(promotion)
  const dates = format_date_span(promotion.start_date, promotion.end_date)
  const products = promotion.products ?? []
  const planned = promotion.planned_amount
  const estimate_cents = estimate == null ? null : Math.round(estimate * 100)
  const planned_cents = planned == null ? null : Math.round(planned * 100)
  const empty = <span class="text-gray-400">—</span>

  return (
    <aside class={`rounded-lg border border-gray-200 bg-gray-50 p-4 ${className ?? ""}`}>
      <div class="flex items-start justify-between gap-2">
        <h4 class="text-sm font-semibold text-gray-900">{promotion.name.trim() || "New promotion"}</h4>
        <span class="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200">{promotion.stage || "Pending"}</span>
      </div>
      <dl class="mt-3 space-y-1.5">
        <Row label="Calendar">{calendar_name || empty}</Row>
        <Row label="Type">{promotion.promotion_type ? `${promotion.promotion_type} · ${PROMOTION_CATEGORY_LABELS[layout.category]}` : empty}</Row>
        <Row label="Account">
          {promotion.customer ? (
            <>
              {promotion.customer}
              {promotion.retailer ? ` → ${promotion.retailer}` : ""}
            </>
          ) : (
            empty
          )}
        </Row>
        <Row label="Dates">{dates ?? empty}</Row>
        <Row label="Products">
          {products.length === 0 ? (
            empty
          ) : products.length <= 3 ? (
            products.join(", ")
          ) : (
            `${products.slice(0, 2).join(", ")} +${products.length - 2} more`
          )}
        </Row>
        {layout.deal_fields !== "none" && (
          <Row label="Deal">
            {deal ?? empty}
            {unit && unit.working && <div class="text-xs text-gray-500">{format_money(unit.amount)}/unit · {unit.working}</div>}
          </Row>
        )}
        {layout.show_volume && (promotion.planned_volume_units || promotion.planned_volume_cases) ? (
          <Row label="Volume">
            {promotion.planned_volume_units ? `${Math.round(promotion.planned_volume_units).toLocaleString("en-US")} units` : ""}
            {promotion.planned_volume_units && promotion.planned_volume_cases ? " · " : ""}
            {promotion.planned_volume_cases ? `${promotion.planned_volume_cases.toLocaleString("en-US")} cases` : ""}
          </Row>
        ) : null}
        {estimate != null && <Row label="Expected trade">{exactCurrencyFormatter(estimate)}</Row>}
        <Row label="Planned amount">
          {planned != null ? <span class="font-semibold">{exactCurrencyFormatter(planned)}</span> : empty}
          {estimate_cents != null && planned_cents != null && estimate_cents !== planned_cents && (
            <div class="text-xs text-amber-700">differs from the estimate</div>
          )}
        </Row>
        {margin && (
          <Row label="Margin">
            {format_pct(margin.regular_margin_pct)} → <span class={margin.promoted_margin < 0 ? "font-semibold text-red-700" : "font-semibold"}>{format_pct(margin.promoted_margin_pct)}</span>
            <div class="text-xs text-gray-500">{format_money(margin.per_unit)} given up per unit</div>
          </Row>
        )}
        <Row label="Owner">{owner_name ?? empty}</Row>
        <Row label="Approver">{approver_name ?? empty}</Row>
        <Row label="Files">{file_count > 0 ? `${file_count} attached` : empty}</Row>
      </dl>
      {missing.length > 0 ? (
        <div class={`mt-3 rounded-md border p-2 text-xs ${attempted ? "border-red-300 bg-red-50 text-red-800" : "border-gray-200 bg-white text-gray-600"}`}>
          <p class="font-medium">Still needed</p>
          <ul class="mt-1 space-y-0.5">
            {missing.map((field) => (
              <li key={field.anchor}>
                <a class="underline" href={`#${field.anchor}`}>
                  {field.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p class="mt-3 text-xs text-emerald-700">Everything required is filled in.</p>
      )}
    </aside>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("format_date_span", () => {
    it("joins the two dates, repeating the year only when it changes", () => {
      expect(format_date_span("2026-03-02", "2026-03-29")).toBe("Mar 2 – Mar 29, 2026")
      expect(format_date_span("2026-12-28", "2027-01-10")).toBe("Dec 28, 2026 – Jan 10, 2027")
      expect(format_date_span("2026-03-02", undefined)).toBe("from Mar 2, 2026")
      expect(format_date_span(undefined, undefined)).toBeNull()
    })
  })

  describe("describe_deal", () => {
    it("phrases each kind of deal", () => {
      expect(describe_deal({ name: "", products: [], promotion_type: "TPR", discount_amount: 1.5, discount_percentage: 15 })).toBe("$1.50 off per unit · 15% off")
      expect(describe_deal({ name: "", products: [], promotion_type: "Multi-Buy", multibuy_quantity: 2, multibuy_price: 6 })).toBe("2 for $6.00")
      expect(describe_deal({ name: "", products: [], promotion_type: "BOGO", bogo_buy_quantity: 1, bogo_get_quantity: 1 })).toBe("Buy 1 get 1 free")
      expect(describe_deal({ name: "", products: [], promotion_type: "Slotting" })).toBeNull()
    })
  })
}
